import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react'
import './toolbar.css'

// 列表页共用的工具栏：搜索 → 常用筛选 →「更多筛选」弹层 → 右侧（分组、视图等）；已选条件在下面一行，可单个去掉。
// 样稿与问题清单见 docs/2026-10-21-排版问题清单.md。

/** 工具栏骨架：左边是搜索和筛选，右边（end）放分组、布局这类「怎么看」的设置。 */
export function Toolbar({ label, children, end }: { label: string; children: ReactNode; end?: ReactNode }) {
  return (
    <div className="lz-toolbar" role="group" aria-label={label}>
      <div className="lz-toolbar-main">{children}</div>
      {end && <div className="lz-toolbar-end">{end}</div>}
    </div>
  )
}

/** 带放大镜的搜索框。label 给读屏，placeholder 说清能搜什么。 */
export function SearchField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="lz-search">
      <Search aria-hidden="true" />
      <input className="lz-input" type="search" aria-label={label} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button type="button" className="lz-search-clear" aria-label={`清空${label}`} onClick={() => onChange('')}>
          <X aria-hidden="true" />
        </button>
      )}
    </label>
  )
}

/** 筛选下拉：字段名一直显示在前面；选了值（不是第一项「全部」）就加深。options 是 <option> 们。 */
export function FilterSelect({ label, ariaLabel, value, onChange, allLabel = '全部', children, wide, neutral }: {
  label: string
  ariaLabel?: string
  value: string
  onChange: (v: string) => void
  allLabel?: string
  children: ReactNode
  wide?: boolean
  /** 分组、排序这类「怎么看」的设置：选了值也不加深（加深只留给真正缩小范围的筛选）。 */
  neutral?: boolean
}) {
  return (
    <label className="lz-filter" data-active={!neutral && value !== ''} data-wide={wide || undefined}>
      <span className="lz-filter-name">{label}</span>
      <select className="lz-select" aria-label={ariaLabel ?? label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {children}
      </select>
      <ChevronDown aria-hidden="true" />
    </label>
  )
}

/** 开关式筛选（复选框画成胶囊）。 */
export function FilterToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="lz-filter-toggle" data-active={checked}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

/** 通用弹层：按钮控制开合；Esc 或点外面关闭，关闭后焦点回到按钮。窄屏上从底部铺满。 */
export function Popover({ button, buttonLabel, panelLabel, children, className, align = 'start', buttonClassName, tone, onOpenChange }: {
  button: ReactNode
  /** 按钮的读屏名；不给就用按钮里的字。 */
  buttonLabel?: string
  panelLabel: string
  children: ReactNode | ((close: () => void) => ReactNode)
  className?: string
  align?: 'start' | 'end'
  buttonClassName?: string
  /** 按钮上的状态点：dirty 有未保存改动，warn 需要注意。 */
  tone?: 'dirty' | 'warn' | 'active'
  onOpenChange?: (open: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const changed = useRef(onOpenChange)
  changed.current = onOpenChange
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    changed.current?.(open)
  }, [open])
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const id = useId()
  const close = () => {
    setOpen(false)
    trigger.current?.focus()
  }
  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close()
      }
    }
    document.addEventListener('mousedown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  return (
    <div ref={root} className={['lz-pop', className].filter(Boolean).join(' ')} data-open={open}>
      <button
        ref={trigger}
        type="button"
        className={['lz-pop-btn', buttonClassName].filter(Boolean).join(' ')}
        aria-label={buttonLabel}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        data-tone={tone}
        onClick={() => setOpen((v) => !v)}
      >
        {button}
      </button>
      {open && (
        <>
          <div className="lz-pop-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <div id={id} className="lz-pop-panel" role="dialog" aria-label={panelLabel} data-align={align}>
            <header className="lz-pop-head">
              <strong>{panelLabel}</strong>
              <button type="button" className="lz-pop-close" aria-label={`关闭${panelLabel}`} onClick={close}>
                <X aria-hidden="true" />
              </button>
            </header>
            {typeof children === 'function' ? children(close) : children}
          </div>
        </>
      )}
    </div>
  )
}

/** 「更多筛选」：按钮上带已选个数，弹层里竖排放不常用的筛选。 */
export function FilterPopover({ count, children }: { count: number; children: ReactNode }) {
  return (
    <Popover
      className="lz-more-filters"
      panelLabel="更多筛选"
      buttonLabel={count ? `更多筛选（已选 ${count} 项）` : '更多筛选'}
      tone={count ? 'active' : undefined}
      button={
        <>
          <SlidersHorizontal aria-hidden="true" />
          <span className="lz-pop-btn-text">更多筛选</span>
          {count > 0 && <span className="lz-count">{count}</span>}
        </>
      }
    >
      <div className="lz-filter-stack">{children}</div>
    </Popover>
  )
}

export interface ActiveFilter {
  key: string
  label: string
  onRemove: () => void
}

/** 已选条件：一行标签，每个可单独去掉，最后是「清除筛选」。没有条件时不占位置。 */
export function ActiveFilters({ items, onClear }: { items: ActiveFilter[]; onClear: () => void }) {
  if (!items.length) return null
  return (
    <div className="lz-active" aria-label="已选条件">
      {items.map((f) => (
        <span key={f.key} className="lz-active-chip">
          {f.label}
          <button type="button" aria-label={`去掉条件：${f.label}`} onClick={f.onRemove}>
            <X aria-hidden="true" />
          </button>
        </span>
      ))}
      <button type="button" className="lz-active-clear" onClick={onClear}>
        清除筛选
      </button>
    </div>
  )
}
